import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Linkedin, 
  Mail, 
  Users, 
  Target, 
  TrendingUp, 
  Settings,
  Play,
  Download,
  BarChart3
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

const AutomatedAcquisitionEngine: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { toast } = useToast();
  const [linkedinUrl, setLinkedinUrl] = React.useState('');
  const [emailSequenceConfig, setEmailSequenceConfig] = React.useState({
    webhookUrl: '',
    targetIndustry: '',
    language: currentLanguage,
    messageTemplate: ''
  });

  const translations = {
    de: {
      title: 'Automatisierte Kundenakquise',
      subtitle: 'LinkedIn Outreach + Email Marketing auf Autopilot',
      linkedinTitle: 'LinkedIn Automation',
      linkedinDesc: 'Automatische Kontaktaufnahme mit 5,000+ CMOs und Marketing-Leadern',
      emailTitle: 'Email Marketing Sequences',
      emailDesc: 'Automatisierte Drip-Kampagnen für verschiedene Zielgruppen',
      setupLinkedin: 'LinkedIn Campaign Konfigurieren',
      setupEmail: 'Email Sequence Konfigurieren',
      targetIndustries: {
        pharma: 'Pharma & Healthcare',
        fintech: 'Fintech & Banking',
        luxury: 'Luxury Brands',
        ngo: 'NGOs & Non-Profit',
        saas: 'SaaS & Tech',
        manufacturing: 'Manufacturing',
        retail: 'Retail & E-Commerce'
      },
      messageTemplates: {
        pharma: 'Hallo {firstName}, ich sah Ihr Profil und war beeindruckt von Ihrer Arbeit bei {company}. Wir helfen Schweizer Pharmaunternehmen dabei, ihre Marketingeffizienz um 340% zu steigern...',
        fintech: 'Hi {firstName}, als Marketing-Lead bei {company} kennen Sie sicher die Herausforderungen im Fintech-Marketing. Unser AI-gestützter Ansatz hat anderen Schweizer Fintech-Unternehmen geholfen...',
        default: 'Hallo {firstName}, ich bewundere die Arbeit, die {company} in der Branche leistet. Unser Schweizer Team spezialisiert sich darauf, Unternehmen wie Ihres digital zu transformieren...'
      },
      webhookUrl: 'Zapier Webhook URL',
      webhookDesc: 'Verbinden Sie Ihre Zapier-Integration für automatische Lead-Weiterleitung',
      language: 'Sprache',
      targetIndustry: 'Zielbranche',
      messageTemplate: 'Nachrichtenvorlage',
      activate: 'Campaign Aktivieren',
      stats: {
        prospects: 'Prospects',
        responseRate: 'Antwortrate',
        meetings: 'Meetings gebucht',
        conversion: 'Conversion Rate'
      }
    },
    en: {
      title: 'Automated Acquisition Engine',
      subtitle: 'LinkedIn Outreach + Email Marketing on Autopilot',
      linkedinTitle: 'LinkedIn Automation',
      linkedinDesc: 'Automatic outreach to 5,000+ CMOs and marketing leaders',
      emailTitle: 'Email Marketing Sequences',
      emailDesc: 'Automated drip campaigns for different target groups',
      setupLinkedin: 'Configure LinkedIn Campaign',
      setupEmail: 'Configure Email Sequence',
      targetIndustries: {
        pharma: 'Pharma & Healthcare',
        fintech: 'Fintech & Banking',
        luxury: 'Luxury Brands',
        ngo: 'NGOs & Non-Profit',
        saas: 'SaaS & Tech',
        manufacturing: 'Manufacturing',
        retail: 'Retail & E-Commerce'
      },
      messageTemplates: {
        pharma: 'Hi {firstName}, I saw your profile and was impressed by your work at {company}. We help Swiss pharmaceutical companies increase their marketing efficiency by 340%...',
        fintech: 'Hi {firstName}, as Marketing Lead at {company}, you surely know the challenges in fintech marketing. Our AI-powered approach has helped other Swiss fintech companies...',
        default: 'Hello {firstName}, I admire the work that {company} is doing in the industry. Our Swiss team specializes in digitally transforming companies like yours...'
      },
      webhookUrl: 'Zapier Webhook URL',
      webhookDesc: 'Connect your Zapier integration for automatic lead forwarding',
      language: 'Language',
      targetIndustry: 'Target Industry',
      messageTemplate: 'Message Template',
      activate: 'Activate Campaign',
      stats: {
        prospects: 'Prospects',
        responseRate: 'Response Rate',
        meetings: 'Meetings Booked',
        conversion: 'Conversion Rate'
      }
    },
    fr: {
      title: 'Moteur d\'Acquisition Automatisé',
      subtitle: 'Prospection LinkedIn + Email Marketing en Autopilote',
      linkedinTitle: 'Automation LinkedIn',
      linkedinDesc: 'Prospection automatique de 5,000+ CMOs et responsables marketing',
      emailTitle: 'Séquences Email Marketing',
      emailDesc: 'Campagnes automatisées pour différents groupes cibles',
      setupLinkedin: 'Configurer Campagne LinkedIn',
      setupEmail: 'Configurer Séquence Email',
      targetIndustries: {
        pharma: 'Pharma & Santé',
        fintech: 'Fintech & Banque',
        luxury: 'Marques de Luxe',
        ngo: 'ONG & Non-Profit',
        saas: 'SaaS & Tech',
        manufacturing: 'Manufacturing',
        retail: 'Retail & E-Commerce'
      },
      messageTemplates: {
        pharma: 'Salut {firstName}, j\'ai vu votre profil et j\'ai été impressionné par votre travail chez {company}. Nous aidons les entreprises pharmaceutiques suisses à augmenter leur efficacité marketing de 340%...',
        fintech: 'Salut {firstName}, en tant que responsable marketing chez {company}, vous connaissez sûrement les défis du marketing fintech. Notre approche alimentée par l\'IA a aidé d\'autres entreprises fintech suisses...',
        default: 'Bonjour {firstName}, j\'admire le travail que {company} fait dans l\'industrie. Notre équipe suisse se spécialise dans la transformation digitale d\'entreprises comme la vôtre...'
      },
      webhookUrl: 'URL Webhook Zapier',
      webhookDesc: 'Connectez votre intégration Zapier pour le transfert automatique de leads',
      language: 'Langue',
      targetIndustry: 'Industrie Cible',
      messageTemplate: 'Modèle de Message',
      activate: 'Activer la Campagne',
      stats: {
        prospects: 'Prospects',
        responseRate: 'Taux de Réponse',
        meetings: 'Rendez-vous Réservés',
        conversion: 'Taux de Conversion'
      }
    },
    it: {
      title: 'Motore di Acquisizione Automatizzato',
      subtitle: 'Prospecting LinkedIn + Email Marketing in Autopilot',
      linkedinTitle: 'Automazione LinkedIn',
      linkedinDesc: 'Prospecting automatico di 5,000+ CMO e responsabili marketing',
      emailTitle: 'Sequenze Email Marketing',
      emailDesc: 'Campagne automatizzate per diversi gruppi target',
      setupLinkedin: 'Configura Campagna LinkedIn',
      setupEmail: 'Configura Sequenza Email',
      targetIndustries: {
        pharma: 'Pharma & Sanità',
        fintech: 'Fintech & Banking',
        luxury: 'Marchi di Lusso',
        ngo: 'ONG & Non-Profit',
        saas: 'SaaS & Tech',
        manufacturing: 'Manufacturing',
        retail: 'Retail & E-Commerce'
      },
      messageTemplates: {
        pharma: 'Ciao {firstName}, ho visto il tuo profilo e sono rimasto colpito dal tuo lavoro presso {company}. Aiutiamo le aziende farmaceutiche svizzere ad aumentare la loro efficienza di marketing del 340%...',
        fintech: 'Ciao {firstName}, come responsabile marketing presso {company}, conosci sicuramente le sfide del marketing fintech. Il nostro approccio basato sull\'AI ha aiutato altre aziende fintech svizzere...',
        default: 'Ciao {firstName}, ammiro il lavoro che {company} sta facendo nel settore. Il nostro team svizzero è specializzato nella trasformazione digitale di aziende come la vostra...'
      },
      webhookUrl: 'URL Webhook Zapier',
      webhookDesc: 'Collega la tua integrazione Zapier per l\'inoltro automatico dei lead',
      language: 'Lingua',
      targetIndustry: 'Settore Target',
      messageTemplate: 'Template Messaggio',
      activate: 'Attiva Campagna',
      stats: {
        prospects: 'Prospects',
        responseRate: 'Tasso di Risposta',
        meetings: 'Meeting Prenotati',
        conversion: 'Tasso di Conversione'
      }
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const handleLinkedinSetup = async () => {
    if (!linkedinUrl) {
      toast({
        title: "Error",
        description: "Please enter your LinkedIn profile URL",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "LinkedIn Campaign Configured",
      description: "Your automated LinkedIn outreach campaign is now active"
    });
  };

  const handleEmailSetup = async () => {
    if (!emailSequenceConfig.webhookUrl) {
      toast({
        title: "Error",
        description: "Please enter your Zapier webhook URL",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(emailSequenceConfig.webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          action: 'setup_email_sequence',
          config: emailSequenceConfig,
          timestamp: new Date().toISOString()
        }),
      });

      toast({
        title: "Email Sequence Activated",
        description: "Your automated email marketing campaign is now running"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate email sequence. Please check your Zapier webhook.",
        variant: "destructive"
      });
    }
  };

  const currentStats = {
    prospects: '12,547',
    responseRate: '23%',
    meetings: '127',
    conversion: '8.2%'
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl text-muted-foreground">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Stats Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
        >
          {Object.entries(currentStats).map(([key, value], index) => (
            <Card key={key} className="neo-card">
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-primary mb-2">{value}</div>
                <div className="text-sm text-muted-foreground">
                  {t.stats[key as keyof typeof t.stats]}
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LinkedIn Automation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="neo-card h-full">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Linkedin className="w-6 h-6 text-blue-600" />
                  <span>{t.linkedinTitle}</span>
                </CardTitle>
                <p className="text-muted-foreground">{t.linkedinDesc}</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">LinkedIn Profile URL</label>
                    <Input
                      value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="neo-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">{t.targetIndustry}</label>
                    <Select onValueChange={(value) => setEmailSequenceConfig(prev => ({ ...prev, targetIndustry: value }))}>
                      <SelectTrigger className="neo-input">
                        <SelectValue placeholder={t.targetIndustry} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(t.targetIndustries).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">{t.messageTemplate}</label>
                    <Textarea
                      value={emailSequenceConfig.messageTemplate}
                      onChange={(e) => setEmailSequenceConfig(prev => ({ ...prev, messageTemplate: e.target.value }))}
                      placeholder={t.messageTemplates.default}
                      className="neo-input min-h-[100px]"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleLinkedinSetup}
                  className="neo-button w-full"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {t.activate}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Email Marketing */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="neo-card h-full">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Mail className="w-6 h-6 text-primary" />
                  <span>{t.emailTitle}</span>
                </CardTitle>
                <p className="text-muted-foreground">{t.emailDesc}</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t.webhookUrl}</label>
                    <Input
                      value={emailSequenceConfig.webhookUrl}
                      onChange={(e) => setEmailSequenceConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      className="neo-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t.webhookDesc}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">{t.language}</label>
                    <Select 
                      value={emailSequenceConfig.language}
                      onValueChange={(value) => setEmailSequenceConfig(prev => ({ ...prev, language: value }))}
                    >
                      <SelectTrigger className="neo-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="it">Italiano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Email Sequence Preview */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email Sequence Preview</label>
                    <div className="space-y-2">
                      {['Welcome', 'Case Study', 'Calculator', 'Audit Call'].map((step, index) => (
                        <div key={step} className="flex items-center space-x-2 p-2 bg-muted rounded">
                          <Badge variant="outline" className="text-xs">{index + 1}</Badge>
                          <span className="text-sm">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleEmailSetup}
                  className="neo-button w-full"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t.activate}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Campaign Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <Card className="neo-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                <span>Campaign Performance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h4 className="font-semibold">Active Campaigns</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm">Pharma Basel</span>
                      <Badge variant="secondary" className="text-xs">Active</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Fintech Zürich</span>
                      <Badge variant="secondary" className="text-xs">Active</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Luxury Geneva</span>
                      <Badge variant="outline" className="text-xs">Paused</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Recent Conversions</h4>
                  <div className="space-y-1 text-sm">
                    <div>SwissPharma AG → Meeting booked</div>
                    <div>TechStart Basel → Calculator used</div>
                    <div>Luxe Geneva → Audit requested</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Next Actions</h4>
                  <div className="space-y-1">
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Export Leads
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Target className="w-4 h-4 mr-2" />
                      Optimize Targeting
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AutomatedAcquisitionEngine;